import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
} from 'react';
import { useAuth } from '../features/auth/AuthContext';
import {
  createWorkspaceState,
  dashboardAddress,
  deserializeWorkspace,
  serializeWorkspace,
  workspaceReducer,
  workspaceStorageKey,
  type WorkspaceAction,
  type WorkspaceState,
} from './workspaceState';

/*
 * The workspace provider holds two things that have to agree with each other but must not
 * re-render together: the layout state, and the DOM elements panes hand back so that a
 * document can be projected into whichever pane currently owns it.
 *
 * They are separate contexts on purpose, and dragging gets a third of its own. A drag
 * changes state many times a second; a document that re-rendered on every one of those
 * frames would make the whole workspace feel like treacle.
 */

const WorkspaceStateContext = createContext<WorkspaceState | null>(null);

export interface WorkspaceActions {
  readonly dispatch: Dispatch<WorkspaceAction>;
  /** Opens an address, reusing an open document at the same address unless `mode` says otherwise. */
  readonly open: (address: string, options?: { readonly mode?: 'reuse' | 'new'; readonly groupId?: string }) => void;
  readonly activate: (panelId: string) => void;
  readonly close: (panelId: string) => void;
  /** Walks one document's own trail, which is not the browser's. */
  readonly travel: (panelId: string, delta: number) => void;
}

const WorkspaceActionsContext = createContext<WorkspaceActions | null>(null);

export interface DockElementRegistry {
  /** Pane and window bodies, kept in state because a document is portalled into them. */
  readonly bodies: Readonly<Record<string, HTMLElement>>;
  readonly registerBody: (hostId: string, element: HTMLElement | null) => void;
  readonly registerFrame: (hostId: string, element: HTMLElement | null) => void;
  readonly registerTabStrip: (hostId: string, element: HTMLElement | null) => void;
  readonly frames: ReadonlyMap<string, HTMLElement>;
  readonly tabStrips: ReadonlyMap<string, HTMLElement>;
  readonly canvas: HTMLElement | null;
  readonly setCanvas: (element: HTMLElement | null) => void;
}

const DockElementContext = createContext<DockElementRegistry | null>(null);

function useRequiredContext<T>(context: React.Context<T | null>, name: string): T {
  const value = useContext(context);
  if (value === null) throw new Error(`${name} was used outside the workspace provider.`);
  return value;
}

export function useWorkspace(): WorkspaceState {
  return useRequiredContext(WorkspaceStateContext, 'useWorkspace');
}

export function useWorkspaceActions(): WorkspaceActions {
  return useRequiredContext(WorkspaceActionsContext, 'useWorkspaceActions');
}

export function useDockElements(): DockElementRegistry {
  return useRequiredContext(DockElementContext, 'useDockElements');
}

function readStoredWorkspace(key: string | null): WorkspaceState | null {
  if (key === null) return null;
  try {
    const raw = globalThis.localStorage?.getItem(key);
    return raw ? deserializeWorkspace(raw) : null;
  } catch {
    return null;
  }
}

/**
 * The arrangement that comes back from storage, with the address actually being asked for
 * open and in front. Without that last step a link followed into a reloaded console would
 * quietly rewrite whichever document happened to be restored in front of it.
 */
function restoreWorkspace(key: string | null, address: string): WorkspaceState {
  const stored = readStoredWorkspace(key);
  if (stored === null) return createWorkspaceState([address]);
  return workspaceReducer(stored, { type: 'open', address });
}

export function WorkspaceProvider({ children, initialAddress }: { children: ReactNode; initialAddress?: string }) {
  const { selectedTenant } = useAuth();
  const tenantId = selectedTenant?.tenantId ?? null;
  const storageKey = workspaceStorageKey(tenantId);

  const [state, dispatch] = useReducer(
    workspaceReducer,
    { storageKey, initialAddress: initialAddress ?? dashboardAddress },
    ({ storageKey: key, initialAddress: address }) => restoreWorkspace(key, address),
  );

  // A tenant switch is a different desk: its own documents, its own arrangement.
  const restoredKey = useRef(storageKey);
  useEffect(() => {
    if (restoredKey.current === storageKey) return;
    restoredKey.current = storageKey;
    dispatch({ type: 'replace', state: restoreWorkspace(storageKey, initialAddress ?? dashboardAddress) });
  }, [initialAddress, storageKey]);

  // Written on a timer rather than on every action, because dragging a splitter is
  // hundreds of actions and none of them is worth a synchronous write to disk.
  useEffect(() => {
    if (storageKey === null) return undefined;
    const handle = globalThis.setTimeout(() => {
      try {
        globalThis.localStorage?.setItem(storageKey, serializeWorkspace(state));
      } catch {
        // A browser with storage turned off still gets the arrangement for this session.
      }
    }, 400);
    return () => globalThis.clearTimeout(handle);
  }, [state, storageKey]);

  const actions = useMemo<WorkspaceActions>(() => ({
    dispatch,
    open: (address, options) => {
      dispatch({
        type: 'open',
        address,
        ...(options?.mode === undefined ? {} : { mode: options.mode }),
        ...(options?.groupId === undefined ? {} : { groupId: options.groupId }),
      });
    },
    activate: (panelId) => dispatch({ type: 'activate', panelId }),
    close: (panelId) => dispatch({ type: 'close', panelId }),
    travel: (panelId, delta) => dispatch({ type: 'travel', panelId, delta }),
  }), []);

  const [bodies, setBodies] = useState<Record<string, HTMLElement>>({});
  const [canvas, setCanvas] = useState<HTMLElement | null>(null);
  const frames = useRef(new Map<string, HTMLElement>());
  const tabStrips = useRef(new Map<string, HTMLElement>());

  const registerBody = useCallback((hostId: string, element: HTMLElement | null) => {
    setBodies((current) => {
      if (element === null) {
        if (current[hostId] === undefined) return current;
        const next = { ...current };
        delete next[hostId];
        return next;
      }
      if (current[hostId] === element) return current;
      return { ...current, [hostId]: element };
    });
  }, []);

  const registerFrame = useCallback((hostId: string, element: HTMLElement | null) => {
    if (element === null) frames.current.delete(hostId);
    else frames.current.set(hostId, element);
  }, []);

  const registerTabStrip = useCallback((hostId: string, element: HTMLElement | null) => {
    if (element === null) tabStrips.current.delete(hostId);
    else tabStrips.current.set(hostId, element);
  }, []);

  const elements = useMemo<DockElementRegistry>(() => ({
    bodies,
    registerBody,
    registerFrame,
    registerTabStrip,
    frames: frames.current,
    tabStrips: tabStrips.current,
    canvas,
    setCanvas,
  }), [bodies, canvas, registerBody, registerFrame, registerTabStrip]);

  return (
    <WorkspaceStateContext.Provider value={state}>
      <WorkspaceActionsContext.Provider value={actions}>
        <DockElementContext.Provider value={elements}>{children}</DockElementContext.Provider>
      </WorkspaceActionsContext.Provider>
    </WorkspaceStateContext.Provider>
  );
}
